import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useIsMutating } from "@tanstack/react-query";
import type mapboxgl from "mapbox-gl";
import { formatTranslation } from "../../../shared/utils/translationUtils";
import { usePlotSelection } from "../context/PlotSelectionContext";
import { useNewTrialState } from "../legacy";
import useCellClick from "./useCellClick";
import useSeedBaseBorders from "./useSeedBaseBorders";
import {
  buildCellGeometryById,
  buildSeedPlotFeatureCollection,
  buildSeedStripeFeatureCollection,
} from "./seedPlotFeatures";
import useSelectionLayers, {
  type CellFeatureInput,
} from "./useSelectionLayers";
import useStripSeedAssignments, {
  stripSeedMutationKey,
} from "../hooks/useStripSeedAssignments";
import { getStripIdentities } from "../services/stripSeedAssignments";
import { buildSeedStripes } from "../utils/seedFlow";
import { getSeedPolygonCoordinates } from "../utils/seedGeometry";
import { getNonEmptySeedColor } from "../utils/stripSeedPresentation";
import { convertSeedRate } from "../services/seedAssignments";
import {
  getSeedProductColor,
  type SeedColorProduct,
} from "../utils/seedProductColors";
import "./seedPlotSelection.less";

interface SeedPlotSelectionMapProps {
  map: mapboxgl.Map | null;
}

const SeedPlotSelectionMap = ({
  map,
}: SeedPlotSelectionMapProps): JSX.Element | null => {
  const { t } = useTranslation();
  const { trialInfo, trialProtocolData, newTrialState } = useNewTrialState();

  const {
    activeScope,
    session,
    focusedId,
    isOutlined,
    toggleTarget,
    pendingConflict,
    openConflict,
    resolveConflict,
    assignedSeedPlots,
    autoAssignmentDraftPlots,
    seedSelectionRates,
    assignedSeedStripes,
    assignedTreatmentPlots,
  } = usePlotSelection();

  const isSeedsPlotScope =
    activeScope?.domain === "seeds" && activeScope.targetMode === "plot";

  const isSeedsStripeScope =
    activeScope?.domain === "seeds" && activeScope.targetMode === "stripe";

  const isBiologicalsStripeScope =
    activeScope?.domain === "biologicals" &&
    activeScope.targetMode === "stripe";

  const isBiologicalsTargetScope = activeScope?.domain === "biologicals";

  const isSelectingSeeds =
    isSeedsPlotScope &&
    session?.tab === "seeds" &&
    session.targetMode === "plot";

  const isSelectingSeedStripes =
    isSeedsStripeScope &&
    session?.tab === "seeds" &&
    session.targetMode === "stripe";

  const isSelectingTreatment =
    isBiologicalsTargetScope &&
    session?.tab === "biologicals" &&
    session.targetMode === activeScope?.targetMode &&
    session.applicationId === activeScope?.applicationId;

  const isSelectingPlot =
    isSelectingSeeds || isSelectingSeedStripes || isSelectingTreatment;

  const preferredInstructionDomain =
    trialProtocolData?.product_details?.seeds != null
      ? "seeds"
      : trialProtocolData?.product_details?.biologicals != null
      ? "biologicals"
      : null;
  const isPreferredInstructionSession =
    isSelectingPlot && activeScope?.domain === preferredInstructionDomain;
  const hasAssignedProducts =
    preferredInstructionDomain === "seeds"
      ? assignedSeedPlots.length > 0 || assignedSeedStripes.length > 0
      : assignedTreatmentPlots.length > 0;

  const stripApi = useStripSeedAssignments(isSeedsStripeScope);

  const stripSaving =
    useIsMutating({
      mutationKey: stripSeedMutationKey(stripApi.trialId, stripApi.scope),
    }) > 0;

  const [geometryById, setGeometryById] = useState<
    Record<string, GeoJSON.Geometry>
  >({});
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const geometryTargetIds = useMemo(
    () => [
      ...new Set([
        ...(isSelectingPlot ? session?.targetIds ?? [] : []),
        ...(isSeedsPlotScope
          ? [
              ...assignedSeedPlots.map((plot) => plot.cellId),
              ...autoAssignmentDraftPlots.map((plot) => plot.cellId),
            ]
          : isSeedsStripeScope
          ? assignedSeedStripes.map((stripe) => stripe.cellId)
          : isBiologicalsTargetScope
          ? assignedTreatmentPlots
              .filter(
                (plot) =>
                  plot.applicationId === activeScope?.applicationId &&
                  (plot.targetMode ?? "plot") === activeScope?.targetMode &&
                  !(isSelectingTreatment && plot.ownerId === session?.ownerId)
              )
              .map((plot) => plot.cellId)
          : []),
      ]),
    ],
    [
      isSelectingPlot,
      session,
      isSeedsPlotScope,
      isSeedsStripeScope,
      isBiologicalsTargetScope,
      isSelectingTreatment,
      activeScope,
      assignedSeedPlots,
      autoAssignmentDraftPlots,
      assignedSeedStripes,
      assignedTreatmentPlots,
    ]
  );

  useEffect(() => {
    setGeometryById({});
  }, [trialInfo?.id]);

  useEffect(() => {
    if (map === null || geometryTargetIds.length === 0) return;

    const targetIds = new Set(geometryTargetIds);

    const captureRenderedGeometry = (): void => {
      if (!map.isStyleLoaded()) return;

      const rendered = buildCellGeometryById(
        (map.queryRenderedFeatures() ?? [])
          .map((feature) => ({
            id: String(feature.properties?.id ?? ""),
            geometry: feature.geometry,
          }))
          .filter(({ id }) => targetIds.has(id))
      );

      setGeometryById((previous) => {
        const changed = Object.keys(rendered).some(
          (id) => JSON.stringify(previous[id]) !== JSON.stringify(rendered[id])
        );

        return changed ? { ...previous, ...rendered } : previous;
      });
    };

    captureRenderedGeometry();
    map.on("idle", captureRenderedGeometry);

    return () => {
      map.off("idle", captureRenderedGeometry);
    };
  }, [map, geometryTargetIds, trialInfo?.id]);

  const designFeatureCollection = useMemo(
    () =>
      isSeedsStripeScope || isBiologicalsStripeScope
        ? buildSeedStripeFeatureCollection(newTrialState.trial_plots)
        : buildSeedPlotFeatureCollection(newTrialState.trial_plots),
    [isSeedsStripeScope, isBiologicalsStripeScope, newTrialState.trial_plots]
  );

  const designGeometryById = useMemo(
    () =>
      buildCellGeometryById(
        designFeatureCollection.features.map((feature) => ({
          id: feature.properties.id,
          geometry: feature.geometry,
        }))
      ),
    [designFeatureCollection]
  );

  const designFeaturePartsById = useMemo(() => {
    const parts: Record<
      string,
      Array<{ geometry: GeoJSON.Geometry; replicant: boolean }>
    > = {};
    designFeatureCollection.features.forEach((feature) => {
      const current = parts[feature.properties.id] ?? [];
      current.push({
        geometry: feature.geometry,
        replicant: feature.properties.replicant === true,
      });
      parts[feature.properties.id] = current;
    });
    return parts;
  }, [designFeatureCollection]);

  const stripGeometryById = useMemo(() => {
    const geometry: Record<string, GeoJSON.Geometry> = {};

    if (!isSeedsStripeScope || stripApi.trial === undefined) {
      return geometry;
    }

    try {
      const stripes = buildSeedStripes({
        trial_plots: newTrialState.trial_plots,
        stripsData: newTrialState.stripsData,
      });

      getStripIdentities(stripApi.trial, stripes).forEach(
        ({ stripe, plotGroupId }) => {
          geometry[stripe.id] = {
            type: "MultiPolygon",
            coordinates: getSeedPolygonCoordinates(
              stripApi.trial?.trial_plots?.[plotGroupId]?.plot?.geojson
                ?.geometry
            ),
          };
        }
      );
    } catch {
      // The Seeds tab reports design/hydration failures.
      // Never guess a mapping from polygon count.
      return {};
    }

    return geometry;
  }, [
    isSeedsStripeScope,
    stripApi.trial,
    newTrialState.trial_plots,
    newTrialState.stripsData,
  ]);

  const geometryFor = useCallback(
    (id: string): GeoJSON.Geometry | undefined =>
      isSeedsStripeScope
        ? stripGeometryById[id]
        : isBiologicalsStripeScope
        ? designGeometryById[id] ?? geometryById[id]
        : geometryById[id] ?? designGeometryById[id],
    [
      isSeedsStripeScope,
      isBiologicalsStripeScope,
      stripGeometryById,
      geometryById,
      designGeometryById,
    ]
  );

  const designPartsFor = useCallback(
    (
      id: string,
      fallback?: GeoJSON.Geometry
    ): Array<{ geometry: GeoJSON.Geometry; replicant: boolean }> => {
      if (activeScope?.domain !== "seeds") {
        return fallback === undefined
          ? []
          : [{ geometry: fallback, replicant: false }];
      }
      return (
        designFeaturePartsById[id] ??
        (fallback === undefined
          ? []
          : [{ geometry: fallback, replicant: false }])
      );
    },
    [designFeaturePartsById, activeScope?.domain]
  );

  const seedColorProducts = useMemo<SeedColorProduct[]>(() => {
    const seedDetails = trialProtocolData?.product_details?.seeds as
      | {
          measure_unit?: string;
          global_density_range?: boolean;
          density_range?: { min?: number; max?: number } | null;
          density_options?: number[] | null;
          products?: Array<{
            id: string;
            variety: string;
            measure_unit?: string;
            density_range?: { min?: number; max?: number } | null;
            density_options?: number[] | null;
          }>;
        }
      | undefined;
    if (!seedDetails) return [];

    const unit = seedDetails.measure_unit ?? "seeds/ha";
    return (seedDetails.products ?? []).map((product) => {
      const settings = seedDetails.global_density_range ? seedDetails : product;
      const sourceUnit = seedDetails.global_density_range
        ? unit
        : product.measure_unit ?? unit;
      const min = settings.density_range?.min;
      const max = settings.density_range?.max;
      return {
        id: product.id,
        name: product.variety,
        options: (settings.density_options ?? []).map((rate) =>
          convertSeedRate(rate, sourceUnit, unit)
        ),
        ...(typeof min === "number" && typeof max === "number"
          ? {
              range: {
                min: convertSeedRate(min, sourceUnit, unit),
                max: convertSeedRate(max, sourceUnit, unit),
              },
            }
          : {}),
      };
    });
  }, [trialProtocolData]);

  const seedDisplayColorFor = useCallback(
    (
      productId: string,
      rate: number | null | undefined,
      fallback: string
    ): string => {
      const product = seedColorProducts.find((item) => item.id === productId);
      if (product === undefined) return fallback;
      return getSeedProductColor(product, seedColorProducts, rate, [rate]);
    },
    [seedColorProducts]
  );

  const handleCellClick = useCallback(
    (cellId: string, geometry: GeoJSON.Geometry): void => {
      if (
        session === null ||
        !session.targetIds.includes(cellId) ||
        session.disabledIds.includes(cellId) ||
        (isSelectingSeedStripes && stripSaving)
      ) {
        return;
      }

      setGeometryById((previous) =>
        previous[cellId] !== undefined
          ? previous
          : { ...previous, [cellId]: geometry }
      );

      if (session.selectedIds.includes(cellId)) {
        toggleTarget(cellId);
        return;
      }

      const owner = isSelectingSeeds
        ? assignedSeedPlots.find((plot) => plot.cellId === cellId)
        : isSelectingSeedStripes
        ? assignedSeedStripes.find((stripe) => stripe.cellId === cellId)
        : isSelectingTreatment
        ? assignedTreatmentPlots.find(
            (plot) =>
              plot.cellId === cellId &&
              plot.applicationId === activeScope?.applicationId &&
              (plot.targetMode ?? "plot") === activeScope?.targetMode
          )
        : undefined;

      if (owner !== undefined && owner.ownerId !== session.ownerId) {
        openConflict({
          tab:
            isSelectingSeeds || isSelectingSeedStripes
              ? "seeds"
              : "biologicals",
          applicationId: isSelectingTreatment
            ? activeScope?.applicationId
            : undefined,
          targetId: cellId,
          ownerId: owner.ownerId,
          ownerLabel: owner.ownerLabel,
        });
        return;
      }

      toggleTarget(cellId);
    },
    [
      session,
      isSelectingSeeds,
      isSelectingSeedStripes,
      isSelectingTreatment,
      stripSaving,
      assignedSeedPlots,
      assignedSeedStripes,
      assignedTreatmentPlots,
      activeScope,
      openConflict,
      toggleTarget,
    ]
  );

  const handleCellHover = useCallback(
    (cell: { cellId: string; geometry: GeoJSON.Geometry } | null): void => {
      setHoveredId(cell?.cellId ?? null);
    },
    []
  );

  useCellClick({
    map,
    enabled:
      isSelectingPlot &&
      pendingConflict === null &&
      !(isSelectingSeedStripes && stripSaving),
    hoverEnabled:
      activeScope?.domain === "seeds" || activeScope?.domain === "biologicals",
    onCellClick: handleCellClick,
    onCellHover: handleCellHover,
  });

  const draftFeatures = useMemo(() => {
    const stripeColorsById = new Map(
      assignedSeedStripes
        .filter((stripe) => stripe.ownerId === session?.ownerId)
        .map((stripe) => [stripe.cellId, stripe.color])
    );

    return (isSelectingPlot ? session?.selectedIds ?? [] : []).reduce<
      CellFeatureInput[]
    >((features, cellId) => {
      const geometry = geometryFor(cellId);

      if (geometry !== undefined) {
        const color = isSelectingSeedStripes
          ? getNonEmptySeedColor(stripeColorsById.get(cellId), session?.color)
          : isSelectingSeeds
          ? seedDisplayColorFor(
              session?.ownerId ?? "",
              seedSelectionRates[cellId],
              session?.color ?? "#FFFFFF"
            )
          : session?.color;
        designPartsFor(cellId, geometry).forEach((part) => {
          features.push({
            cellId,
            geometry: part.geometry,
            color,
            ...(isSelectingSeeds || isSelectingSeedStripes
              ? { seed: true }
              : {}),
            ...(part.replicant ? { replicant: true } : {}),
            outlined: isOutlined(cellId),
          });
        });
      }

      return features;
    }, []);
  }, [
    isSelectingPlot,
    isSelectingSeedStripes,
    session,
    geometryFor,
    designPartsFor,
    isSelectingSeeds,
    seedDisplayColorFor,
    seedSelectionRates,
    assignedSeedStripes,
    isOutlined,
  ]);

  const assignedFeatures = useMemo(() => {
    const features: CellFeatureInput[] = [];

    if (isSeedsPlotScope) {
      assignedSeedPlots.forEach((plot) => {
        if (isSelectingSeeds && plot.ownerId === session?.ownerId) return;

        const geometry = geometryFor(plot.cellId);

        if (geometry !== undefined) {
          designPartsFor(plot.cellId, geometry).forEach((part) => {
            features.push({
              cellId: plot.cellId,
              geometry: part.geometry,
              color: plot.color,
              seed: true,
              outlined: isOutlined(plot.cellId),
              ...(part.replicant ? { replicant: true } : {}),
            });
          });
        }
      });
    } else if (isSeedsStripeScope) {
      assignedSeedStripes.forEach((stripe) => {
        if (isSelectingSeedStripes && stripe.ownerId === session?.ownerId) {
          return;
        }

        const geometry = geometryFor(stripe.cellId);

        if (geometry !== undefined) {
          designPartsFor(stripe.cellId, geometry).forEach((part) => {
            features.push({
              cellId: stripe.cellId,
              geometry: part.geometry,
              color: stripe.color,
              seed: true,
              outlined: isOutlined(stripe.cellId),
              ...(part.replicant ? { replicant: true } : {}),
            });
          });
        }
      });
    } else if (isBiologicalsTargetScope) {
      assignedTreatmentPlots.forEach((plot) => {
        if (
          plot.applicationId !== activeScope?.applicationId ||
          (plot.targetMode ?? "plot") !== activeScope?.targetMode ||
          (isSelectingTreatment && plot.ownerId === session?.ownerId)
        ) {
          return;
        }

        const geometry = geometryFor(plot.cellId);

        if (geometry !== undefined) {
          designPartsFor(plot.cellId, geometry).forEach((part) => {
            features.push({
              cellId: plot.cellId,
              geometry: part.geometry,
              color: plot.color,
              outlined: isOutlined(plot.cellId),
              ...(part.replicant ? { replicant: true } : {}),
            });
          });
        }
      });
    }

    return features;
  }, [
    isSeedsPlotScope,
    isSeedsStripeScope,
    isBiologicalsTargetScope,
    isSelectingSeeds,
    isSelectingSeedStripes,
    isSelectingTreatment,
    assignedSeedPlots,
    assignedSeedStripes,
    assignedTreatmentPlots,
    isOutlined,
    activeScope,
    session?.ownerId,
    geometryFor,
    designPartsFor,
    seedDisplayColorFor,
  ]);

  const autoAssignmentDraftFeatures = useMemo(() => {
    if (!isSeedsPlotScope) return [];

    return autoAssignmentDraftPlots.reduce<CellFeatureInput[]>(
      (features, plot) => {
        const geometry = geometryFor(plot.cellId);

        if (geometry !== undefined) {
          designPartsFor(plot.cellId, geometry).forEach((part) => {
            features.push({
              cellId: plot.cellId,
              geometry: part.geometry,
              color: plot.color,
              seed: true,
              outlined: isOutlined(plot.cellId),
              ...(part.replicant ? { replicant: true } : {}),
            });
          });
        }

        return features;
      },
      []
    );
  }, [
    isSeedsPlotScope,
    autoAssignmentDraftPlots,
    geometryFor,
    designPartsFor,
    isOutlined,
  ]);

  const focusedFeatures = useMemo(() => {
    if (
      focusedId === null ||
      !isOutlined(focusedId) ||
      (activeScope?.domain !== "seeds" && activeScope?.domain !== "biologicals")
    ) {
      return [];
    }

    const geometry = isSeedsStripeScope
      ? stripGeometryById[focusedId]
      : designGeometryById[focusedId] ?? geometryById[focusedId];

    return geometry === undefined
      ? []
      : designPartsFor(focusedId, geometry).map((part) => ({
          cellId: focusedId,
          geometry: part.geometry,
          ...(activeScope?.domain === "seeds" ? { seed: true } : {}),
          ...(part.replicant ? { replicant: true } : {}),
        }));
  }, [
    focusedId,
    isOutlined,
    activeScope?.domain,
    isSeedsStripeScope,
    stripGeometryById,
    designGeometryById,
    geometryById,
    designPartsFor,
  ]);

  const hoveredFeatures = useMemo(() => {
    if (
      hoveredId === null ||
      (session?.selectedIds.includes(hoveredId) ?? false) ||
      (activeScope?.domain !== "seeds" && activeScope?.domain !== "biologicals")
    ) {
      return [];
    }
    const geometry = geometryFor(hoveredId);
    if (geometry === undefined) return [];
    const assignedOwner = isSeedsPlotScope
      ? assignedSeedPlots.find((plot) => plot.cellId === hoveredId)
      : isSeedsStripeScope
      ? assignedSeedStripes.find((stripe) => stripe.cellId === hoveredId)
      : undefined;
    const shadow =
      session?.tab === "seeds" &&
      assignedOwner !== undefined &&
      assignedOwner.ownerId !== session.ownerId;
    return designPartsFor(hoveredId, geometry).map((part) => ({
      cellId: hoveredId,
      geometry: part.geometry,
      ...(part.replicant ? { replicant: true } : {}),
      shadow,
    }));
  }, [
    hoveredId,
    session,
    activeScope?.domain,
    isSeedsPlotScope,
    isSeedsStripeScope,
    assignedSeedPlots,
    assignedSeedStripes,
    geometryFor,
    designPartsFor,
  ]);

  const outlineFeatures = useMemo(() => {
    if (
      activeScope?.domain !== "seeds" &&
      activeScope?.domain !== "biologicals"
    ) {
      return [];
    }
    const features: CellFeatureInput[] = designFeatureCollection.features.map(
      (feature) => ({
        cellId: feature.properties.id,
        geometry: feature.geometry,
        ...(activeScope.domain === "seeds" ? { seed: true } : {}),
        ...(feature.properties.replicant === true ? { replicant: true } : {}),
        outlined: isOutlined(feature.properties.id),
      })
    );
    if (isSeedsStripeScope) {
      Object.entries(stripGeometryById).forEach(([cellId, geometry]) => {
        features.push({
          cellId,
          geometry,
          seed: true,
          outlined: isOutlined(cellId),
        });
      });
    }
    return features;
  }, [
    activeScope?.domain,
    isSeedsStripeScope,
    designFeatureCollection,
    stripGeometryById,
    isOutlined,
  ]);

  useSeedBaseBorders(
    map,
    activeScope?.domain === "seeds" && outlineFeatures.length > 0
  );

  useSelectionLayers({
    map,
    assigned: assignedFeatures,
    autoAssignmentDraft: autoAssignmentDraftFeatures,
    draft: draftFeatures,
    focused: focusedFeatures,
    hovered: hoveredFeatures,
    outlines: outlineFeatures,
    keepSeedLayersAboveGrid: activeScope?.domain === "seeds",
    keepHighlightsAboveGrid: activeScope?.domain === "biologicals",
  });

  const showInstruction =
    isPreferredInstructionSession &&
    !hasAssignedProducts &&
    (session?.selectedIds.length ?? 0) === 0;

  if (!isSelectingPlot) return null;

  if (pendingConflict !== null) {
    const conflictText = formatTranslation(
      t(
        pendingConflict.tab === "biologicals"
          ? session?.targetMode === "stripe"
            ? "newRatesAndDosage.assign.conflictStripeTreatmentText"
            : "newRatesAndDosage.assign.conflictTreatmentText"
          : session?.targetMode === "stripe"
          ? "newRatesAndDosage.assign.conflictStripeText"
          : "newRatesAndDosage.assign.conflictText"
      )
    );

    return (
      <div className="rd-plot-selection-conflict" role="alertdialog">
        <p className="rd-plot-selection-conflict__text">
          {conflictText}
          {pendingConflict.ownerLabel ? ` (${pendingConflict.ownerLabel})` : ""}
        </p>

        <div className="rd-plot-selection-conflict__actions">
          <button
            type="button"
            disabled={isSelectingSeedStripes && stripSaving}
            onClick={() => {
              resolveConflict(false);
            }}
          >
            {formatTranslation(t("newRatesAndDosage.assign.keep"))}
          </button>

          <button
            type="button"
            className="rd-plot-selection-conflict__reassign"
            disabled={isSelectingSeedStripes && stripSaving}
            onClick={() => {
              resolveConflict(true);
            }}
          >
            {formatTranslation(t("newRatesAndDosage.assign.reassign"))}
          </button>
        </div>
      </div>
    );
  }

  if (!showInstruction) return null;

  return (
    <div className="rd-seed-selection-instruction" role="status">
      <InfoCircleOutlined
        className="rd-seed-selection-instruction__icon"
        aria-hidden
      />
      <div className="rd-seed-selection-instruction__content">
        <strong className="rd-seed-selection-instruction__title">
          {formatTranslation(
            t(
              session?.targetMode === "stripe"
                ? "newRatesAndDosage.assign.bannerStripesTitle"
                : "newRatesAndDosage.assign.bannerTitle"
            )
          )}
        </strong>

        <span className="rd-seed-selection-instruction__text">
          {formatTranslation(
            t(
              session?.targetMode === "stripe"
                ? "newRatesAndDosage.assign.bannerStripesText"
                : "newRatesAndDosage.assign.bannerText"
            )
          )}
        </span>
      </div>
    </div>
  );
};

export default SeedPlotSelectionMap;
